"use strict";
/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

var __importDefault = void 0 && (void 0).__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});

var ConditionalFormatsModel_1 = __importDefault(require("./models/ConditionalFormatsModel"));

var ConditionalFormatSpecHelper_1 = require("../definitions/properties/util/ConditionalFormatSpecHelper");

var UIPanelModel_1 = __importDefault(require("../ui/mst/UIPanelModel"));

var Actions_1 = require("../definitions/constants/Actions");

var mobx_state_tree_1 = require("mobx-state-tree");

var VALUE_FROM_CONTENT_MODEL = 'ValueFromContentModel';

var ConditionalFormattingState =
/** @class */
function () {
  function ConditionalFormattingState(conditionalFormattingApi, visType, conditionalFormattingSpec) {
    this._conditionalFormattingApi = conditionalFormattingApi;
    this._visType = visType;
    this._conditionalFormattingSpec = conditionalFormattingSpec;
    this._mstForUIPanels = UIPanelModel_1.default.create({}, {
      measures: this._conditionalFormattingApi.getConditionalFormatMeasures(),
      getMSTConditionalFormatModelFn: this._getMSTConditionalFormatModel.bind(this)
    });
  }

  ConditionalFormattingState.prototype.destroy = function () {
    if (this._mst) {
      mobx_state_tree_1.destroy(this._mst);
    }
  };

  ConditionalFormattingState.prototype.initialize = function () {
    return Promise.resolve();
  };

  ConditionalFormattingState.prototype.setUIContext = function (uiContext) {
    this._uiContext = uiContext;

    this._mstForUIPanels.setUIContext(uiContext);
  };

  ConditionalFormattingState.prototype.getStateModel = function () {
    return this._getStateModel();
  };

  ConditionalFormattingState.prototype.isKpiVis = function () {
    return ConditionalFormatSpecHelper_1.ConditionalFormatSpecHelper.isKpiVis(this._visType);
  };

  ConditionalFormattingState.prototype.getConditionalFormattingApi = function () {
    return this._conditionalFormattingApi;
  };

  ConditionalFormattingState.prototype.setMeasureDataItem = function (measure) {
    this._mstForUIPanels.setMeasureDataItem(measure);
  };

  ConditionalFormattingState.prototype.selectMeasureAddedToSlot = function (measure) {
    var _this = this; // Load the palettes for the selected measure after it was added to slot
    // just in case the Properties Panel is open so that palettes can be display in the UI


    return this._conditionalFormattingApi.slotMappingIsReady().then(function () {
      if (measure) {
        var mstModel = _this._getMSTConditionalFormatModel(measure.getDataItemId()); // Load the palette so that it gets updated correctly in the UI during component update
        // This case usually ocur with the Properties Panel open and build-up (Smarts build-up)


        return mstModel.loadPalette().then(_this._mstForUIPanels.setMeasureDataItem.bind(_this._mstForUIPanels, measure));
      }

      _this._mstForUIPanels.setMeasureDataItem(measure);

      return Promise.resolve();
    });
  };

  ConditionalFormattingState.prototype.setMeasures = function (measures) {
    this._mstForUIPanels.setMeasures(measures);
  };

  ConditionalFormattingState.prototype.setCurrentUIPanelId = function (value) {
    this._mstForUIPanels.setPanelId(value);
  };

  ConditionalFormattingState.prototype.resetCurrentUIPanelId = function () {
    this._mstForUIPanels.resetPanelId();
  };

  ConditionalFormattingState.prototype.setHasTargetValue = function (value) {
    this._mstForUIPanels.setHasTargetValue(value);
  };

  ConditionalFormattingState.prototype.setExpandRuleFlag = function (dataItemId, ruleIndex, expand) {
    var rule = this._conditionalFormattingApi ? this._conditionalFormattingApi.getRule(dataItemId, ruleIndex) : null;

    if (rule) {
      rule.setExpand(expand);
    }
  };

  ConditionalFormattingState.prototype.getConditionalFormatModel = function () {
    return this._mstForUIPanels.getMSTConditionalFormatModel();
  };

  ConditionalFormattingState.prototype.getMstForUIPanels = function () {
    return this._mstForUIPanels;
  };

  ConditionalFormattingState.prototype.updateVisType = function (visType) {
    if (this._visType !== visType) {
      this._visType = visType;

      this._conditionalFormattingSpec.updateVisType(visType);
    }
  };

  ConditionalFormattingState.prototype.applyModelChanges = function (changeContext) {
    if (!this._hasMSTModelForUI()) {
      return;
    }

    var mst = this._getStateModel();

    if (changeContext.action === Actions_1.ACTIONS.SET_SUMMARY_FILL_COLOR) {
      mst["" + changeContext.action + VALUE_FROM_CONTENT_MODEL](changeContext.value);
    } else {
      if (!changeContext.dataItemId) {
        throw new Error('Invalid changeContext.dataItemId provided');
      }

      var condFormatModel = this._getMSTConditionalFormatModel(changeContext.dataItemId);

      if (!condFormatModel) {
        return;
      }

      switch (changeContext.action) {
        case Actions_1.ACTIONS.SET_SCALE:
        case Actions_1.ACTIONS.SET_PALETTE_ID:
        case Actions_1.ACTIONS.SET_BACKGROUND_OPACITY:
        case Actions_1.ACTIONS.SET_COLOR_BY:
        case Actions_1.ACTIONS.DELETE_RULE:
        case Actions_1.ACTIONS.SET_HAS_MIN_MAX:
          condFormatModel["" + changeContext.action + VALUE_FROM_CONTENT_MODEL](changeContext.value);
          break;

        case Actions_1.ACTIONS.ADD_RULE:
          condFormatModel["" + changeContext.action + VALUE_FROM_CONTENT_MODEL](changeContext.ruleIndex, changeContext.value);
          break;

        case Actions_1.ACTIONS.MOVE_RULE:
          condFormatModel["" + changeContext.action + VALUE_FROM_CONTENT_MODEL](changeContext.value, changeContext.ruleIndex);
          break;

        case Actions_1.ACTIONS.SET_RANGE_OPERATOR:
        case Actions_1.ACTIONS.SET_START_CONDITION_TYPE:
        case Actions_1.ACTIONS.SET_START_CONDITION_VALUE:
        case Actions_1.ACTIONS.SET_END_CONDITION_TYPE:
        case Actions_1.ACTIONS.SET_END_CONDITION_VALUE:
        case Actions_1.ACTIONS.SET_RULE_STYLE_FILL_COLOR:
        case Actions_1.ACTIONS.SET_RULE_STYLE_TEXT_COLOR:
        case Actions_1.ACTIONS.SET_RULE_STYLE_SHAPE:
          {
            var ruleModel = condFormatModel.getRuleByIndex(changeContext.ruleIndex);

            if (ruleModel) {
              ruleModel["" + changeContext.action + VALUE_FROM_CONTENT_MODEL](changeContext.value, changeContext.context);
            }
          }
          break;

        default:
          break;
      }
    }
  };

  ConditionalFormattingState.prototype._getStateModel = function () {
    if (!this._mst) {
      this._mst = ConditionalFormatsModel_1.default.create(this._conditionalFormattingSpec.getMSTJsonSpec(), {
        getVisType: this.getVisType.bind(this),
        conditionalFormattingApi: this._conditionalFormattingApi,
        uiContext: this._uiContext
      });
    }

    return this._mst;
  };

  ConditionalFormattingState.prototype._getMSTConditionalFormatModel = function (dataItemId) {
    var mst = this._getStateModel();

    var mstModel = mst.getConditionalFormatById(dataItemId);

    if (!mstModel) {
      var jsonModel = this._conditionalFormattingSpec.getConditionalFormatById(dataItemId, true).getMSTJsonSpec();

      if (!jsonModel) {
        throw new Error("jsonModel not found for " + dataItemId + " during _getMSTConditionalFormatModel call");
      }

      mstModel = mst.addConditionalFormat(jsonModel);
    }

    return mstModel;
  };

  ConditionalFormattingState.prototype._hasMSTModelForUI = function () {
    return !!this._mst;
  };

  ConditionalFormattingState.prototype.getVisType = function () {
    return this._visType;
  };

  return ConditionalFormattingState;
}();

exports.default = ConditionalFormattingState;
